/**
 * Inbox 工作台的狀態機：對話清單、選取、已讀、目前對話的事件串。
 *
 * 只依賴 `ApiClient` 介面，不直接 fetch、不假設後端在不在（同 useChatApi 的邊界）。
 * 排序規則（CONSOLE-UX §3.2）：
 *   1. `unread` 多的在前
 *   2. 同未讀數時 `lastActivityAt` 新的在前（回完 → unread=0 → 自然沉到未讀之下）
 * 未讀是後端權威（`GET /api/sessions` 的 `unread`）；WS 只在「不是目前對話、且事件比已知的最後活動新」
 * 時先本地 +1（見 `applyMessageToConversation`）。`hello.recent` 的歷史重播**不算**未讀——
 * 它每次連線都會重播整個緩衝，算進去會讓收件匣數字變成人工作階段的兩倍（實測 1138 vs 571）。
 */
import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toConversation } from '../types';
import { expectsEcho } from '../delivery';
import { errorKindOf } from '../api/data-state';
import { PEER_VERIFY_DELAY_MS, peerOutcomeMessage, pickPeerCandidates, verifyByPeer, } from '../delivery-peer';
import { proofPath } from '../delivery-verify';
import { normalizeMessageText } from '../message-text';
/** 未讀優先 → 最後活動時間新到舊。null 視為 0（從沒活動的排最後）。 */
export function compareConversations(a, b) {
    if (b.unread !== a.unread)
        return b.unread - a.unread;
    return (b.lastActivityAt ?? 0) - (a.lastActivityAt ?? 0);
}
/** 事件指紋：同毫秒、同類型、同人、同字、同對象視為同一則（WS 重播會重送）。 */
function fingerprint(event) {
    return `${event.ts}:${event.type}:${event.from}:${event.text}:${event.to ?? ''}`;
}
/** 事件 → 帶 id 的 InboxEvent；重複指紋用序號區分，避免兩則真的一模一樣的訊息被吃掉。
 *
 * 第一筆**不加**序號：WS 即時 append 用的 id 是 `${accountId}:${key}`，兩邊格式一致
 * React 才認得出「同一列」，`ThreadRow` 的 memo 與 DOM 都能原地沿用；加上 `#1` 會讓
 * 每次 `GET events` 對帳都變成整串重新掛載（實測 33 列全部重繪）。
 */
function toInboxEvents(accountId, events) {
    const used = new Map();
    return events.map((event) => {
        const key = fingerprint(event);
        const seen = (used.get(key) ?? 0) + 1;
        used.set(key, seen);
        return { ...event, accountId, id: seen === 1 ? `${accountId}:${key}` : `${accountId}:${key}#${seen}` };
    });
}
/**
 * 本地樂觀訊息 ↔ 站方回播的配對鍵。
 *
 * 刻意**不含 `ts`**：本地那則是瀏覽器時鐘，回播是站方時間，兩者一定差幾秒（實測 7 秒），
 * 用 `fingerprint`（含 ts）比對永遠不成立 → 同一句會變成兩則。這裡改用「同帳號＋同類型＋
 * 同發話人＋同內容＋同對象」，時間只當窗戶（`OUTGOING_MATCH_WINDOW_MS`）。
 */
export function outgoingKey(accountId, event) {
    return `${accountId}|${event.type}|${event.from}|${event.text}|${event.to ?? ''}`;
}
/** 回播與本地送出時間的允許差距：時鐘偏移容忍 10 分鐘。 */
const OUTGOING_MATCH_WINDOW_MS = 10 * 60_000;
/**
 * 送出成功後維持「傳送中」的上限：ek21 靠輪詢回播（實測最慢約 7–10 秒），
 * 超過這個時間就當作不會有回播。到期的處理分兩種（見 `delivery.ts`）：
 * - 這個站會回播自己（ek21）：轉成一般訊息。
 * - 這個站不會回播自己（UT 的公開訊息／私訊，0014／DELIVERY §4.2）：標「已送出（站方無回執）」，
 *   不要讓它看起來跟「有人看到的一般訊息」一樣，也不要一直灰著像卡住。
 */
export const OUTGOING_SETTLE_MS = 12_000;
/** 配對紀錄的保存上限（給晚到的回播用）；過期就丟，免得愈積愈多。 */
const OUTGOING_KEY_TTL_MS = 30 * 60_000;
/**
 * 在 `pending` 裡找「這則回播對應的本地那則」，回傳索引（找不到回 -1）。
 *
 * 抽成純函式是為了能直接驗：回播的 `ts` 一定和本地不同（站方時間 vs 瀏覽器時間），
 * 所以比對不用 `ts`、只用內容鍵＋時間窗；同內容連送兩次時取**最早那筆**（FIFO），
 * 一則站方事件只能吃掉一則本地訊息。
 */
export function findOutgoingMatch(pending, accountId, event, now = Date.now()) {
    const key = outgoingKey(accountId, event);
    return pending.findIndex((item) => item.accountId === accountId &&
        item.key === key &&
        Math.abs(item.ts - event.ts) <= OUTGOING_MATCH_WINDOW_MS &&
        Math.abs(now - item.ts) <= OUTGOING_KEY_TTL_MS);
}
/**
 * 一則 WS 訊息進來後，這個對話的未讀怎麼變（純函式，給 hook 與驗證腳本共用）。
 *
 * 三條規則，缺一條就會出現「收件匣未讀 1138、工作階段 571」這種打臉數字：
 * 1. `replay`（`hello.recent` 歷史重播）**不算未讀**——連線／重連時後端會把整個
 *    recent 緩衝（上限 200 筆）重播一次，算進去就等於把後端未讀翻倍。
 * 2. `ts` 不比已知的最後活動新 → 不算未讀（舊事件補進來只補畫面）。
 * 3. 目前正在看的對話不算未讀。
 *
 * `isNew` 是給呼叫端更新 `lastActivityAt`／預覽用的：重播的舊事件不該把時間軸往回拉。
 */
export function applyMessageToConversation(current, message, activeId, accountId) {
    const isNew = message.replay !== true && message.ts > (current.lastActivityAt ?? 0);
    if (!isNew || accountId === activeId)
        return { unread: current.unread, isNew };
    return { unread: current.unread + 1, isNew };
}
export function useInbox(options) {
    const { client, maxEvents = 300, autoLoad = true, eventLimit = 200 } = options;
    const [conversations, setConversations] = useState([]);
    const [dropped, setDropped] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [activeEvents, setActiveEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [errorKind, setErrorKind] = useState(null);
    const [conversationsLoaded, setConversationsLoaded] = useState(false);
    const [eventsError, setEventsError] = useState(null);
    const [eventsErrorKind, setEventsErrorKind] = useState(null);
    /** 目前選取的 id；給 subscribe 的閉包讀（不重訂閱）。 */
    const activeRef = useRef(null);
    /** 目前清單；`markInvalid` 要在 callback 裡讀最新值（不重綁）。 */
    const conversationsRef = useRef([]);
    /** events 請求序號：切對話時舊回應不覆蓋新對話。 */
    const eventsReqRef = useRef(0);
    const eventsSeenRef = useRef(new Set());
    /** 本地先插入、還沒被回播確認的訊息（配對與去重都靠它）。 */
    const pendingRef = useRef([]);
    /**
     * 已送出那幾則的完整原文（`id → PendingLocal`）。
     *
     * `pendingRef` 會在結案時被清掉（UT 不會有回播，留著只會被長得像的訊息吃掉），
     * 但「用第二個帳號確認」是**結案之後**才按的，所以原文要另存一份；重整就沒了（本來就不該留）。
     */
    const sentRef = useRef(new Map());
    /** 自動（只讀）第二連線驗證的計時器，卸載時要收乾淨。 */
    const peerTimersRef = useRef(new Set());
    /** 已由回播／歷史對帳確認的訊息鍵（`outgoingKey`）：重抓歷史後仍要保留「已確認送達」標籤。 */
    const confirmedKeysRef = useRef(new Set());
    /** 本地訊息序號：給 React key，不依賴時間避免同一毫秒撞號。 */
    const pendingSeqRef = useRef(0);
    /** 「傳送中」轉一般訊息的計時器（送出成功才裝）。 */
    const settleTimersRef = useRef(new Map());
    /**
     * 已處理過的 WS 訊息指紋。
     *
     * `hello`／`state` 信封會把 `recent` 整批重播（實測一次 200 則），重播的每一則都會再走一次
     * `onMessage`（未讀 +1、清單複製、事件去重）。重播不是新訊息，整個跳過；指紋含 `ts`，
     * 真正的新訊息不會撞。
     */
    const seenMessagesRef = useRef(new Set());
    activeRef.current = activeId;
    conversationsRef.current = conversations;
    const clearSettleTimer = useCallback((localId) => {
        const timer = settleTimersRef.current.get(localId);
        if (timer === undefined)
            return;
        clearTimeout(timer);
        settleTimersRef.current.delete(localId);
    }, []);
    /**
     * 找一則「就是這則回播」的本地訊息並消費掉它（回傳本地 id）。
     * 同內容重複送（連按兩次「好」）時，依送出順序一則配一則，不會兩則都被同一則吃掉。
     */
    const takeMatchingPending = useCallback((accountId, event) => {
        const now = Date.now();
        pendingRef.current = pendingRef.current.filter((item) => item.accountId !== accountId || Math.abs(now - item.ts) <= OUTGOING_KEY_TTL_MS);
        const index = findOutgoingMatch(pendingRef.current, accountId, event, now);
        const hit = index === -1 ? undefined : pendingRef.current.splice(index, 1)[0];
        if (!hit)
            return null;
        clearSettleTimer(hit.id);
        return hit.id;
    }, [clearSettleTimer]);
    /**
     * 這則送出訊息「該問誰」：同房另一顆身分（公開訊息）或收件人本人（私訊）。
     * 回 null＝資料不足（舊呼叫端沒帶原文），只能顯示「沒有可用的第二個身分」。
     */
    const peerQueryFor = useCallback((item) => {
        if (!item.type || typeof item.text !== 'string')
            return null;
        const self = conversationsRef.current.find((row) => row.id === item.accountId);
        if (!self)
            return null;
        return {
            self,
            query: {
                service: self.service,
                kind: item.type,
                from: item.from ?? self.nickname,
                text: item.text,
                to: item.to,
                ts: item.ts,
            },
        };
    }, []);
    /**
     * 用第二個連線驗證一則送出訊息（只讀；最多 3 次 `GET /events`）。
     *
     * 為什麼這是 UT 唯一能拿到「真的到了」的路徑：UT 不回播自己（`delivery.ts`），
     * 但同房其他身分看得到（`docs/DELIVERY.md` §5-B 第 6 步實測），
     * 所以叫我們自己的另一顆去讀列表，看到同一句＝站方真的把這則發出去了。
     * 看到就標 `confirmed`＋`pendingProof:'peer'`；沒看到照實說「還沒出現」，不假裝失敗。
     */
    const verifyPeer = useCallback(async (localId) => {
        const item = sentRef.current.get(localId);
        if (!item)
            return null;
        const target = peerQueryFor(item);
        if (!target)
            return null;
        const candidates = pickPeerCandidates(conversationsRef.current, target.self, target.query);
        setActiveEvents((prev) => prev.map((event) => event.id === localId
            ? { ...event, pending: 'verifying', pendingError: undefined }
            : event));
        const outcome = client.listEvents && candidates.length > 0
            ? await verifyByPeer(client, candidates, target.query)
            : {
                status: candidates.length === 0 ? 'no-peer' : 'error',
                checks: [],
                latencyMs: 0,
                at: Date.now(),
            };
        setActiveEvents((prev) => prev.map((event) => {
            if (event.id !== localId)
                return event;
            if (outcome.status === 'verified') {
                confirmedKeysRef.current.add(item.key);
                return {
                    ...event,
                    pending: 'confirmed',
                    pendingProof: 'peer',
                    pendingError: undefined,
                };
            }
            return { ...event, pending: 'unconfirmed', pendingError: peerOutcomeMessage(outcome) };
        }));
        return outcome;
    }, [client, peerQueryFor]);
    useEffect(() => {
        const unsubscribe = client.subscribe({
            onMessage: (message) => {
                // 重播去重：同帳號同時間同人同字同對象＝同一則，重播不再重算（`.recent` 一次可達 200 則）。
                // 只對「已經在清單裡」的帳號生效：還沒進清單的帳號照舊走，避免漏掉第一次的事件。
                const messageKey = `${message.accountId}|${message.ts}|${message.kind}|${message.from}|${message.to ?? ''}|${message.text}`;
                const knownAccount = conversationsRef.current.some((item) => item.id === message.accountId);
                if (knownAccount && seenMessagesRef.current.has(messageKey))
                    return;
                seenMessagesRef.current.add(messageKey);
                if (seenMessagesRef.current.size > 4000) {
                    // 只保留最近的 2000 筆（WS 指紋會一直長；這裡沒有時間可排序，整批換掉最舊的一半）。
                    seenMessagesRef.current = new Set([...seenMessagesRef.current].slice(-2000));
                }
                setConversations((prev) => {
                    const index = prev.findIndex((item) => item.id === message.accountId);
                    if (index === -1)
                        return prev;
                    const current = prev[index];
                    if (!current)
                        return prev;
                    // 未讀規則集中在 applyMessageToConversation（重播不算、舊事件不算、目前對話不算）；
                    // 時間軸與預覽只跟著 isNew 動，重播的舊事件不把清單往回拉。
                    const { unread, isNew } = applyMessageToConversation(current, message, activeRef.current, message.accountId);
                    const lastActivityAt = isNew ? message.ts : current.lastActivityAt;
                    const lastMessagePreview = isNew ? normalizeMessageText(message.text) : current.lastMessagePreview;
                    // 三項都沒變就不要換陣列（換了等於整棵樹重繪一次）。
                    if (current.unread === unread &&
                        current.lastActivityAt === lastActivityAt &&
                        current.lastMessagePreview === lastMessagePreview) {
                        return prev;
                    }
                    const next = [...prev];
                    next[index] = { ...current, unread, lastActivityAt, lastMessagePreview };
                    return next;
                });
                // 目前對話的事件：append + 去重。
                if (message.accountId !== activeRef.current)
                    return;
                const event = {
                    type: message.kind,
                    from: message.from,
                    text: message.text,
                    ts: message.ts,
                    ...(message.to ? { to: message.to } : {}),
                    ...(message.room ? { room: message.room } : {}),
                };
                const key = fingerprint(event);
                if (eventsSeenRef.current.has(key))
                    return;
                eventsSeenRef.current.add(key);
                // 這一則可能就是剛送出那句的回播：直接取代本地那則，不要變成兩則。
                const localId = takeMatchingPending(message.accountId, event);
                setActiveEvents((prev) => {
                    if (localId) {
                        confirmedKeysRef.current.add(outgoingKey(message.accountId, event));
                        if (confirmedKeysRef.current.size > 500) {
                            confirmedKeysRef.current = new Set([...confirmedKeysRef.current].slice(-250));
                        }
                    }
                    const server = {
                        ...event,
                        accountId: message.accountId,
                        id: `${message.accountId}:${key}`,
                        ...(localId ? { pending: 'confirmed', pendingProof: 'echo' } : {}),
                    };
                    if (localId) {
                        const index = prev.findIndex((item) => item.id === localId);
                        if (index !== -1) {
                            const replaced = [...prev];
                            replaced[index] = server;
                            return replaced;
                        }
                    }
                    const next = [...prev, server];
                    return next.length > maxEvents ? next.slice(next.length - maxEvents) : next;
                });
            },
            onAccount: (account) => {
                setConversations((prev) => {
                    const index = prev.findIndex((item) => item.id === account.id);
                    if (index === -1)
                        return prev;
                    const current = prev[index];
                    if (!current)
                        return prev;
                    // 狀態信封很常重送同樣的內容；內容沒變就不要換陣列（換了等於整棵樹重繪）。
                    const same = Object.entries(account).every(([key, value]) => Object.is(current[key], value));
                    if (same)
                        return prev;
                    const next = [...prev];
                    next[index] = { ...current, ...account };
                    return next;
                });
            },
            onError: (message) => setError(message),
        });
        return unsubscribe;
    }, [client, maxEvents, takeMatchingPending]);
    const load = useCallback(async () => {
        setLoading(true);
        try {
            // 優先取帶 unread 的完整版；舊實作只有 listAccounts 時退回（未讀會是 0）。
            const rows = client.listConversations
                ? await client.listConversations()
                : (await client.listAccounts()).map((row) => toConversation(row));
            setConversations(rows);
            setActiveId((current) => current ?? rows[0]?.id ?? null);
            setError(null);
            setErrorKind(null);
            setConversationsLoaded(true);
        }
        catch (err) {
            // 關鍵（#02）：失敗時**不要**清空 conversations，也不要把錯誤吞成一條 banner；
            // error／errorKind 留著讓清單顯示「連不上＋重試」，而不是「還沒有對話」。
            setError(errMessage(err));
            setErrorKind(errorKindOf(err));
        }
        finally {
            setLoading(false);
        }
    }, [client]);
    useEffect(() => {
        if (autoLoad)
            void load();
    }, [autoLoad, load]);
    const loadEvents = useCallback(async (id, loadOptions = {}) => {
        // quiet＝送出後／背景對帳：不要讓對話串閃一下「載入中」。
        const quiet = loadOptions.quiet === true;
        const token = (eventsReqRef.current += 1);
        if (!quiet)
            setEventsLoading(true);
        eventsSeenRef.current = new Set();
        try {
            if (!client.listEvents) {
                throw new Error('這個 ApiClient 沒有 listEvents；請改用 createHttpApiClient（GET /api/sessions/:id/events）');
            }
            const page = await client.listEvents(id, { limit: eventLimit });
            if (token !== eventsReqRef.current)
                return;
            const list = toInboxEvents(id, page.events).map((item) => confirmedKeysRef.current.has(outgoingKey(id, item))
                ? { ...item, pending: 'confirmed' }
                : item);
            // 歷史裡的某幾則可能就是本地那幾則（剛送出、已回播但 ts 不同）→ 先配對並記下來，
            // 下面合併時把本地版本丟掉，站方版本以歷史為準。
            const consumedLocalIds = new Set();
            for (const event of page.events) {
                const localId = takeMatchingPending(id, event);
                if (localId)
                    consumedLocalIds.add(localId);
            }
            // GET 幾百毫秒內到達的 WS 新訊息不能因為「歷史重設」被蓋掉：以歷史為底，補上已收到的。
            setActiveEvents((prev) => {
                // 對帳回來的歷史要**盡量沿用舊的物件**：id 相同＝內容相同，直接拿 prev 那一筆，
                // 這樣 `ThreadRow` 的 memo 不會因為「重建了一份一樣的事件」而整串重繪。
                const prevById = new Map(prev.map((event) => [event.id, event]));
                const reuse = (event) => prevById.get(event.id) ?? event;
                const merged = list.map(reuse);
                const have = new Set(list.map((event) => fingerprint(event)));
                for (const event of prev) {
                    if (consumedLocalIds.has(event.id))
                        continue;
                    const key = fingerprint(event);
                    if (have.has(key))
                        continue;
                    have.add(key);
                    merged.push(event);
                }
                merged.sort((a, b) => a.ts - b.ts);
                const next = merged.slice(-maxEvents);
                eventsSeenRef.current = new Set(next.map((event) => fingerprint(event)));
                // 左欄預覽也順手對齊：最後一則「真的是一句話」的事件（沒有 from 的 system 是後端紀錄，不算）。
                const newest = [...next].reverse().find((event) => !(event.type === 'system' && event.from.trim() === ''));
                if (newest) {
                    // 預覽可能是站方 JSON 系統訊息／img: 標記，先正規化再寫進清單。
                    const newestPreview = normalizeMessageText(newest.text);
                    setConversations((rows) => rows.map((item) => {
                        if (item.id !== id)
                            return item;
                        if ((item.lastActivityAt ?? 0) > newest.ts)
                            return item;
                        return {
                            ...item,
                            lastActivityAt: newest.ts,
                            lastMessagePreview: newestPreview !== '' ? newestPreview : item.lastMessagePreview,
                        };
                    }));
                }
                return next;
            });
            setError(null);
            setErrorKind(null);
            setEventsError(null);
            setEventsErrorKind(null);
        }
        catch (err) {
            if (token !== eventsReqRef.current)
                return;
            if (!quiet)
                setActiveEvents([]);
            setError(errMessage(err));
            setErrorKind(errorKindOf(err));
            setEventsError(errMessage(err));
            setEventsErrorKind(errorKindOf(err));
        }
        finally {
            if (token === eventsReqRef.current && !quiet)
                setEventsLoading(false);
        }
    }, [client, eventLimit, maxEvents, takeMatchingPending]);
    const select = useCallback((id) => {
        setActiveId(id);
        activeRef.current = id;
    }, []);
    // 首次自動選中第一個對話時也要把事件拉回來（select 沒被呼叫）。
    useEffect(() => {
        if (activeId)
            void loadEvents(activeId);
        else {
            eventsReqRef.current += 1;
            eventsSeenRef.current = new Set();
            setActiveEvents([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeId]);
    const markRead = useCallback(async (id) => {
        const target = id ?? activeRef.current;
        if (!target)
            return;
        // 先本地歸零，清單立刻沉底；失敗再回報。
        // 已經 0 就不要換陣列（換了整棵樹重繪一次，送出後對帳會白跑一趟）。
        setConversations((prev) => {
            const current = prev.find((item) => item.id === target);
            if (!current)
                return prev;
            if (current.unread === 0)
                return prev;
            return prev.map((item) => item.id === target ? { ...item, unread: 0, lastReadTs: Date.now() } : item);
        });
        if (!client.markRead) {
            setError('這個 ApiClient 沒有 markRead；未讀無法回寫後端（POST /api/sessions/:id/read）');
            return;
        }
        try {
            const result = await client.markRead(target);
            setConversations((prev) => prev.map((item) => item.id === target ? { ...item, unread: result.unread, lastReadTs: result.lastReadTs } : item));
        }
        catch (err) {
            setError(errMessage(err));
        }
    }, [client]);
    const markInvalid = useCallback((id, reason) => {
        const target = conversationsRef.current.find((item) => item.id === id) ?? null;
        const remaining = conversationsRef.current.filter((item) => item.id !== id);
        setConversations(remaining);
        setDropped((prev) => {
            const row = {
                id,
                nickname: target?.nickname ?? id,
                service: target?.service ?? 'ek21',
                room: target?.room ?? null,
                reason: reason ?? '後端已回收這個 session',
                at: Date.now(),
            };
            return prev.some((item) => item.id === id)
                ? prev.map((item) => (item.id === id ? row : item))
                : [...prev, row];
        });
        // 目前對話死掉就換到還活著的（不要留一個送不出去的空白對話）。
        if (activeRef.current === id) {
            const next = remaining[0] ?? null;
            activeRef.current = next?.id ?? null;
            setActiveId(next?.id ?? null);
        }
    }, []);
    const dismissDropped = useCallback((id) => {
        setDropped((prev) => prev.filter((item) => item.id !== id));
    }, []);
    /**
     * 送出**前** → 本地先插入一則灰色「傳送中…」（樂觀更新）。
     *
     * 為什麼要在 POST 之前：`say`／`pm` 只花 ~60ms，但站方回播靠輪詢（實測 7003ms）。
     * 先畫上去，使用者打完字立刻看到自己那句；POST 成功後交 `markOutgoingDelivered`，
     * 失敗交 `failOutgoing`（灰轉紅、可重送）。
     *
     * 去重：本地 id 是 `local:<account>:<seq>` 並登記在 `pendingRef`。站方回播（WS 或 GET events）
     * 到達時由 `takeMatchingPending` 以「帳號＋類型＋發話人＋內容＋對象＋10 分鐘窗」配對，
     * **取代**本地那則而不是再 append，所以不會出現兩則。
     */
    const recordOutgoing = useCallback((input) => {
        const ts = typeof input.ts === 'number' ? input.ts : Date.now();
        const text = input.text;
        const accountId = input.accountId;
        const localId = `local:${accountId}:${(pendingSeqRef.current += 1)}`;
        const base = {
            type: input.type,
            from: input.from,
            text,
            ts,
            ...(input.to ? { to: input.to } : {}),
        };
        const record = {
            id: localId,
            accountId,
            key: outgoingKey(accountId, base),
            ts,
            expectsEcho: expectsEcho(input.service, input.type),
            ...(input.service ? { service: input.service } : {}),
            type: input.type,
            from: input.from,
            text,
            ...(input.to ? { to: input.to } : {}),
        };
        pendingRef.current.push(record);
        sentRef.current.set(localId, record);
        // 只留最近 50 則的原文（`Map` 保序，刪最舊的）；不讓它在長工作階段裡無限長大。
        while (sentRef.current.size > 50) {
            const oldest = sentRef.current.keys().next().value;
            if (oldest === undefined)
                break;
            sentRef.current.delete(oldest);
        }
        // 左欄：預覽／時間一定要動，否則「送出了」只發生在對話串，清單還寫著上一句。
        //
        // 但這裡是**非緊急**更新：它要重排／重繪整份清單（實測 195 列時單次 commit 300ms+），
        // 和下面那則「灰字」搶同一個 commit 就會讓使用者按 Enter 後一個 frame 看不到自己那句話。
        // 包進 startTransition 之後 React 先提交輸入框與灰字（緊急），清單預覽晚一步補上；
        // 去重用的 pendingRef 與事件內容都是同步寫入的（下面那段不受影響），只是「畫清單」降級。
        startTransition(() => {
            setConversations((prev) => prev.map((item) => {
                if (item.id !== accountId)
                    return item;
                const viewing = accountId === activeRef.current;
                return {
                    ...item,
                    // 與對話串顯示一致：自己送出的那句話也走同一套正規化（img: 標記不裸露出現）。
                    lastMessagePreview: normalizeMessageText(text),
                    lastActivityAt: ts,
                    unread: viewing ? 0 : item.unread,
                    ...(viewing ? { lastReadTs: ts } : {}),
                };
            }));
        });
        if (accountId === activeRef.current) {
            setActiveEvents((prev) => {
                const next = [...prev, { ...base, accountId, id: localId, pending: 'sending' }];
                return next.length > maxEvents ? next.slice(next.length - maxEvents) : next;
            });
        }
        return localId;
    }, [maxEvents]);
    /**
     * POST 成功：維持「傳送中」，回播到達（去重時換掉）或超過 `OUTGOING_SETTLE_MS` 才結案。
     *
     * 結案分兩種（判斷見 `delivery.ts`）：
     * - 站方會回播（ek21 兩種、UT 沒有）：照舊轉一般訊息。
     * - 站方不會回播（UT 公開發言／私訊）：**不要**轉一般訊息——那會讓使用者以為對方看到了。
     *   改標「已受理 14:32」（帶受理時間），並把配對紀錄清掉（不會有回播，留著只會被長得像的訊息吃掉）。
     *   結案的同時**自動**叫第二個連線（同房另一顆身分）讀一次列表（只讀；`delivery-peer.ts`）：
     *   看到就升級成「已確認送達」，沒看到就照實說「還沒出現」。重整後這則會消失（歷史裡沒有它）。
     */
    const markOutgoingDelivered = useCallback((localId) => {
        clearSettleTimer(localId);
        const pending = pendingRef.current.find((item) => item.id === localId);
        const silent = pending?.expectsEcho === false;
        const timer = setTimeout(() => {
            settleTimersRef.current.delete(localId);
            if (silent) {
                pendingRef.current = pendingRef.current.filter((item) => item.id !== localId);
            }
            setActiveEvents((prev) => prev.map((item) => item.id === localId
                ? { ...item, pending: silent ? 'unconfirmed' : 'awaiting' }
                : item));
            // 自動取證（只讀）：站方不回播的站才需要，而且要有第二個身分可問。
            // 不是「自動重送」——POST 已被受理，重送等於對真人多發一次同一句話。
            if (!silent || !pending)
                return;
            const target = peerQueryFor(pending);
            if (!target)
                return;
            if (proofPath(target.query.service, target.query.kind) === 'echo')
                return;
            const candidates = pickPeerCandidates(conversationsRef.current, target.self, target.query);
            if (candidates.length === 0)
                return;
            const peerTimer = setTimeout(() => {
                peerTimersRef.current.delete(peerTimer);
                void verifyPeer(localId);
            }, PEER_VERIFY_DELAY_MS);
            peerTimersRef.current.add(peerTimer);
        }, OUTGOING_SETTLE_MS);
        settleTimersRef.current.set(localId, timer);
    }, [clearSettleTimer, peerQueryFor, verifyPeer]);
    /** POST 失敗：標成「送出失敗」留在串上（重送才會在有上下文的情況下按得到）。 */
    const failOutgoing = useCallback((localId, reason) => {
        clearSettleTimer(localId);
        pendingRef.current = pendingRef.current.filter((item) => item.id !== localId);
        sentRef.current.delete(localId);
        setActiveEvents((prev) => prev.map((item) => item.id === localId
            ? { ...item, pending: 'failed', ...(reason ? { pendingError: reason } : {}) }
            : item));
    }, [clearSettleTimer]);
    /** 重送前先拿掉舊的失敗那則，否則重送成功後同一句會看到兩則。 */
    const discardOutgoing = useCallback((localId) => {
        clearSettleTimer(localId);
        pendingRef.current = pendingRef.current.filter((item) => item.id !== localId);
        sentRef.current.delete(localId);
        setActiveEvents((prev) => prev.filter((item) => item.id !== localId));
    }, [clearSettleTimer]);
    // 卸載時把「傳送中 → 一般」與「自動第二連線驗證」的計時器收乾淨
    // （切頁／換身分時不要對已卸載的 state 設值，也不要在背景打站方）。
    useEffect(() => () => {
        for (const timer of settleTimersRef.current.values())
            clearTimeout(timer);
        settleTimersRef.current.clear();
        for (const timer of peerTimersRef.current)
            clearTimeout(timer);
        peerTimersRef.current.clear();
    }, []);
    /** 送出後／回到前景時主動對帳一次；quiet 不閃 loading，失敗已有 error 提示，不往外丟。 */
    const refreshEvents = useCallback(async (id) => {
        const target = id ?? activeRef.current;
        if (!target)
            return;
        if (client.listEvents)
            await loadEvents(target, { quiet: true });
        // 使用者正看著這個對話：自己送出的訊息不該在左欄留紅點，順便把後端未讀歸零。
        // （mock client 沒有 markRead，不要因此跳出「未讀無法回寫」的假錯誤。）
        if (target === activeRef.current && client.markRead)
            await markRead(target);
    }, [client, loadEvents, markRead]);
    const sorted = useMemo(() => [...conversations].sort(compareConversations), [conversations]);
    const active = useMemo(() => conversations.find((item) => item.id === activeId) ?? null, [activeId, conversations]);
    return {
        conversations: sorted,
        activeId,
        active,
        activeEvents,
        loading,
        eventsLoading,
        error,
        errorKind,
        conversationsLoaded,
        eventsError,
        eventsErrorKind,
        dropped,
        select,
        markRead,
        markInvalid,
        dismissDropped,
        recordOutgoing,
        markOutgoingDelivered,
        failOutgoing,
        discardOutgoing,
        refreshEvents,
        verifyPeer,
        refresh: load,
        clearError: () => {
            setError(null);
            setErrorKind(null);
        },
    };
}
function errMessage(err) {
    return err instanceof Error ? err.message : String(err);
}
//# sourceMappingURL=useInbox.js.map